import React from 'react'
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers'
import { useController, UseControllerProps, useFormContext } from 'react-hook-form'
import { format, isValid, parse, parseISO } from 'date-fns'

const DATE_STORAGE_FORMAT = 'yyyy-MM-dd'

const parseFormDateValue = (value: string | Date): Date | null => {
    if (value instanceof Date) {
        return isValid(value) ? value : null
    }
    const datePart = value.slice(0, 10)
    const parsed = parse(datePart, DATE_STORAGE_FORMAT, new Date())
    return isValid(parsed) ? parsed : null
}

const parsePastedDate = (text: string): Date | null => {
    const trimmed = text.trim().replace(/[\u2010-\u2015\u2212]/g, '-')
    if (!trimmed) {
        return null
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        const iso = parseISO(trimmed.slice(0, 10))
        if (isValid(iso)) {
            return iso
        }
    }

    const formats = [DATE_STORAGE_FORMAT, 'yyyy-M-d']
    for (const fmt of formats) {
        const parsed = parse(trimmed, fmt, new Date())
        if (isValid(parsed)) {
            return parsed
        }
    }

    return null
}

// RegisteredDatePicker Component
function RegisteredDatePicker({
    name,
    validation,
    ...props
}: DatePickerProps & {
    name: string
    validation?: UseControllerProps['rules']
}) {
    const { control } = useFormContext()

    // Using react-hook-form's useController to manage DatePicker state
    const {
        field: { onChange, value },
        fieldState: { error },
    } = useController({
        name,
        control,
        rules: validation,
    })

    // Handle date change and format to 'yyyy-MM-dd' before updating react-hook-form state
    const handleDateChange = (date: Date | null) => {
        if (date && isValid(date)) {
            onChange(format(date, DATE_STORAGE_FORMAT))
        } else {
            onChange(null)
        }
    }

    const handlePaste = (event: React.ClipboardEvent) => {
        const date = parsePastedDate(event.clipboardData.getData('text'))
        if (!date) {
            return
        }
        event.preventDefault()
        event.stopPropagation()
        handleDateChange(date)
    }

    return (
        <DatePicker
            {...props}
            format={props.format ?? DATE_STORAGE_FORMAT}
            value={value ? parseFormDateValue(value) : null}
            onChange={handleDateChange}
            slotProps={{
                field: { onPaste: handlePaste },
                textField: {
                    error: !!error,
                    helperText: error ? error.message : '',
                    variant: 'outlined',
                    slotProps: {
                        inputLabel: {
                            shrink: true,
                        },
                    },
                },
            }}
        />
    )
}

export { RegisteredDatePicker }
